import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faChartLine,
  faHeadset,
  faRocket,
  faShieldAlt,
  faMobileAlt,
  faGift,
  faUsers,
  faStar,
  faCheckCircle,
  faArrowRight,
  faCalendarAlt,
  faBrain,
  faBullhorn,
  faCrosshairs,
  faLightbulb,
  faCogs,
  faArrowTrendUp
} from '@fortawesome/free-solid-svg-icons';

function Home() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      icon: faBrain,
      title: "IA Generativa para Marketing",
      description: "Crea campañas, contenido y estrategias de marketing impulsadas por inteligencia artificial avanzada.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: faCrosshairs,
      title: "Segmentación Inteligente",
      description: "Identifica y conecta con tu audiencia ideal usando análisis de datos y machine learning.",
      color: "from-indigo-500 to-indigo-600"
    },
    {
      icon: faArrowTrendUp,
      title: "Optimización Automática",
      description: "Mejora continuamente el rendimiento de tus campañas con IA que aprende y optimiza en tiempo real.",
      color: "from-green-500 to-green-600"
    },
    {
      icon: faBullhorn,
      title: "Campañas Omnicanal",
      description: "Gestiona todas tus campañas desde una sola plataforma: email, SMS, redes sociales y más.",
      color: "from-orange-500 to-orange-600"
    },
    {
      icon: faLightbulb,
      title: "Insights Predictivos",
      description: "Anticipa tendencias y comportamientos de clientes con análisis predictivo avanzado.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: faCogs,
      title: "Automatización Completa",
      description: "Automatiza todo tu marketing desde la creación hasta la optimización con flujos inteligentes.",
      color: "from-red-500 to-red-600"
    }
  ];

  const testimonials = [
    {
      name: "Equipo de Desarrollo",
      role: "PetitPrime",
      content: "Estamos emocionados de lanzar PetitPrime como tu copiloto de marketing impulsado por IA. Nuestra plataforma está diseñada para hacer el marketing accesible y efectivo para todos los negocios.",
      rating: 5,
      isInternal: true
    },
    {
      name: "Beta Testers",
      role: "Usuarios Piloto",
      content: "Los primeros usuarios que han probado PetitPrime están impresionados con la capacidad de la IA para generar contenido relevante y optimizar campañas automáticamente.",
      rating: 5,
      isInternal: true
    },
    {
      name: "Expertos en Marketing Digital",
      role: "Consultores",
      content: "PetitPrime democratiza el marketing digital con IA, permitiendo que cualquier negocio tenga acceso a estrategias avanzadas de crecimiento.",
      rating: 5,
      isInternal: true
    }
  ];

  const stats = [
    { number: "30 días", label: "Prueba Gratuita" },
    { number: "0€", label: "Sin Tarjeta de Crédito" },
    { number: "10x", label: "Más Efectivo" },
    { number: "24/7", label: "IA Siempre Activa" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>PetitPrime - Copiloto de Marketing con IA para Negocios | 30 Días Gratis</title>
        <meta name="description" content="Transforma tu negocio con PetitPrime. Tu copiloto de marketing impulsado por IA que crea campañas, optimiza automáticamente y hace crecer tu negocio. Prueba gratis 30 días." />
        <meta name="keywords" content="marketing IA, copiloto marketing, automatización marketing, campañas IA, crecimiento negocio, marketing digital" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative container mx-auto px-4 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Tu Copiloto de Marketing
                <span className="text-yellow-400 block">Impulsado por IA</span>
              </h1>
              <p className="text-xl mb-8 text-blue-100 leading-relaxed">
                PetitPrime es tu compañero de marketing inteligente. Crea campañas,
                optimiza automáticamente y haz crecer tu negocio con la potencia
                de la inteligencia artificial.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link
                  to="/register"
                  className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faRocket} className="mr-2" />
                  Prueba Gratis 30 Días
                </Link>
                <Link
                  to="https://calendly.com/info-petitstores/30min"
                  className="border-2 border-white hover:bg-white hover:text-blue-600 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                  Agenda una Demo
                </Link>
              </div>
              <div className="flex items-center text-sm text-blue-200">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-400" />
                Sin tarjeta de crédito • IA siempre activa • Resultados garantizados
              </div>
            </div>
            <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FontAwesomeIcon icon={faBrain} className="text-2xl text-blue-900" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">IA Inteligente</h3>
                    <p className="text-blue-200 mb-4">Tu copiloto personal de marketing</p>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="bg-white/10 rounded-lg p-3">
                        <div className="text-2xl font-bold text-yellow-400">Crea</div>
                        <div className="text-sm text-blue-200">Contenido</div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-3">
                        <div className="text-2xl font-bold text-green-400">Optimiza</div>
                        <div className="text-sm text-blue-200">Automáticamente</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Todo lo que Necesitas para Hacer Crecer tu Negocio
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              PetitPrime combina la potencia de la IA con herramientas de marketing
              avanzadas para que cualquier negocio pueda competir con los grandes.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <FontAwesomeIcon icon={feature.icon} className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="py-20 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Ve PetitPrime en Acción
            </h2>
            <p className="text-xl text-gray-600">
              Descubre cómo la IA puede transformar tu marketing en menos de 3 minutos
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
              <iframe
                width="100%"
                height="500"
                src="https://www.youtube.com/embed/sGHYOPPSMlY"
                title="Demo PetitPrime - Copiloto de Marketing con IA"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Lo que Dicen de PetitPrime
            </h2>
            <p className="text-xl text-gray-600">
              Nuestro equipo y expertos en marketing digital
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-100">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon={faStar} className="text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <div className="font-bold text-gray-800">{testimonial.name}</div>
                  <div className="text-purple-600">{testimonial.role}</div>
                  {testimonial.isInternal && (
                    <div className="text-xs text-gray-500 mt-1">Equipo interno</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            ¿Listo para Revolucionar tu Marketing?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Únete a PetitPrime y deja que la IA haga crecer tu negocio
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faRocket} className="mr-2" />
              Comenzar Prueba Gratis
              <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
            </Link>
            <Link
              to="/pricing"
              className="border-2 border-white hover:bg-white hover:text-blue-600 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 flex items-center justify-center"
            >
              Ver Planes y Precios
            </Link>
          </div>
          <div className="mt-8 text-blue-200 text-sm">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-400" />
            Sin compromiso • IA incluida • Resultados garantizados
          </div>
        </div>
      </section>

      {/* Footer Info */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          {/* <div className="flex justify-center mb-6">
            <img src="logos/youtoo.png" alt="Logo Youtoo" className="h-12" />
          </div> */}
          <p className="text-gray-600 mb-4">
            ¿Tienes preguntas sobre marketing con IA? Nuestro equipo está aquí para ayudarte
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="mailto:info@petitstores.com"
              className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
            >
              <FontAwesomeIcon icon={faHeadset} className="mr-2" />
              info@petitstores.com
            </a>
            <Link
              to="/about"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Conoce Nuestro Equipo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
